import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, X, Loader2 } from "lucide-react";
import { useCreateAIModel, useUpdateAIModel } from "@/hooks/useAIInventory";
import { DataSourceMultiSelect, FRAMEWORK_OPTIONS, modelStatusLabels } from "./shared";
import { toast } from "sonner";

interface ModelsTabProps {
  aiModels: any[];
  dataSources: any[];
  businessAreas: any[];
  orgId: string | null;
  isSuperAdmin: boolean;
  selectedOrg: string;
  profileOrgId?: string;
  onDeleteConfirm: (item: { type: string; id: string; name: string }) => void;
}

export default function ModelsTab({ aiModels, dataSources, businessAreas, orgId, isSuperAdmin, selectedOrg, profileOrgId, onDeleteConfirm }: ModelsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");

  const [newModel, setNewModel] = useState({ name: "", type: "Predictivo", framework: "", version: "", owner: "", department: "", description: "", training_data_sources: [] as string[] });

  const createModel = useCreateAIModel();
  const updateModel = useUpdateAIModel();

  const types = useMemo(() => [...new Set(aiModels.map((m: any) => m.type).filter(Boolean))], [aiModels]);
  const depts = useMemo(() => [...new Set(aiModels.map((m: any) => m.department).filter(Boolean))], [aiModels]);

  const filtered = useMemo(() => {
    return aiModels.filter((model: any) => {
      const matchesSearch = (model.name || '').toLowerCase().includes(search.toLowerCase()) ||
                           (model.framework || '').toLowerCase().includes(search.toLowerCase()) ||
                           (model.department || '').toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === "all" || model.type === typeFilter;
      const matchesStatus = statusFilter === "all" || model.status === statusFilter;
      const matchesDept = deptFilter === "all" || model.department === deptFilter;
      return matchesSearch && matchesType && matchesStatus && matchesDept;
    });
  }, [aiModels, search, typeFilter, statusFilter, deptFilter]);

  const getOrgIdForInsert = () => {
    if (isSuperAdmin) {
      if (selectedOrg === 'all') { toast.error('Selecciona una organización específica'); return null; }
      return selectedOrg;
    }
    return profileOrgId;
  };

  const handleAdd = () => {
    const oid = getOrgIdForInsert();
    if (!oid) return;
    createModel.mutate({
      organization_id: oid, ...newModel, status: 'development', accuracy: 0, infra_cost: 0,
    }, {
      onSuccess: () => {
        setNewModel({ name: "", type: "Predictivo", framework: "", version: "", owner: "", department: "", description: "", training_data_sources: [] });
        setIsDialogOpen(false);
      }
    });
  };

  const handleEdit = () => {
    if (!editing) return;
    updateModel.mutate({
      id: editing.id, name: editing.name, type: editing.type, framework: editing.framework,
      version: editing.version, owner: editing.owner, department: editing.department,
      description: editing.description, status: editing.status, accuracy: editing.accuracy,
      infra_cost: editing.infra_cost, training_data_sources: editing.training_data_sources || [],
    }, { onSuccess: () => setEditing(null) });
  };

  const ModelTypeSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="NLP">NLP</SelectItem>
        <SelectItem value="Computer Vision">Computer Vision</SelectItem>
        <SelectItem value="Predictivo">Predictivo</SelectItem>
        <SelectItem value="Generativo">Generativo</SelectItem>
        <SelectItem value="Clasificación">Clasificación</SelectItem>
        <SelectItem value="Otro">Otro</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle>Modelos de IA Propios</CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Agregar Modelo</Button></DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Nuevo Modelo de IA</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Nombre</Label><Input value={newModel.name} onChange={(e) => setNewModel({...newModel, name: e.target.value})} placeholder="Ej: Modelo de Churn" /></div>
                    <div className="space-y-2"><Label>Tipo</Label><ModelTypeSelect value={newModel.type} onChange={(v) => setNewModel({...newModel, type: v})} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Framework</Label>
                      <Select value={newModel.framework} onValueChange={(v) => setNewModel({...newModel, framework: v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar framework" /></SelectTrigger>
                        <SelectContent>{FRAMEWORK_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Versión</Label><Input value={newModel.version} onChange={(e) => setNewModel({...newModel, version: e.target.value})} placeholder="Ej: 1.0" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Responsable</Label><Input value={newModel.owner} onChange={(e) => setNewModel({...newModel, owner: e.target.value})} placeholder="Equipo o persona" /></div>
                    <div className="space-y-2">
                      <Label>Departamento</Label>
                      <Select value={newModel.department} onValueChange={(v) => setNewModel({...newModel, department: v})}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar departamento" /></SelectTrigger>
                        <SelectContent>{businessAreas.map((a: any) => <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>Descripción</Label><Textarea value={newModel.description} onChange={(e) => setNewModel({...newModel, description: e.target.value})} placeholder="Qué hace este modelo" rows={2} /></div>
                  <div className="space-y-2">
                    <Label>Fuentes de Entrenamiento</Label>
                    <DataSourceMultiSelect selected={newModel.training_data_sources} onChange={(t) => setNewModel({...newModel, training_data_sources: t})} dataSources={dataSources} />
                  </div>
                  <Button onClick={handleAdd} className="w-full" disabled={!newModel.name || !newModel.framework || createModel.isPending}>
                    {createModel.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Agregar Modelo
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar modelo..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              {search && <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => setSearch("")}><X className="h-3 w-3" /></Button>}
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los tipos</SelectItem>{types.map((t: string) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="production">Producción</SelectItem>
                <SelectItem value="testing">Testing</SelectItem>
                <SelectItem value="development">Desarrollo</SelectItem>
                <SelectItem value="retired">Retirado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{depts.map((d: string) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            {(search || typeFilter !== "all" || statusFilter !== "all" || deptFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setTypeFilter("all"); setStatusFilter("all"); setDeptFilter("all"); }}>Limpiar filtros</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modelo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Framework</TableHead>
                <TableHead>Versión</TableHead>
                <TableHead>Fuentes</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead className="text-center">Accuracy</TableHead>
                <TableHead className="text-right">Costo Infra/mes</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">{aiModels.length === 0 ? 'No hay modelos registrados' : 'No se encontraron modelos con los filtros aplicados'}</TableCell></TableRow>
              ) : filtered.map((model: any) => (
                <TableRow key={model.id}>
                  <TableCell>
                    <div><div className="font-medium">{model.name}</div><div className="text-xs text-muted-foreground">{model.description}</div></div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{model.type}</Badge></TableCell>
                  <TableCell className="text-sm">{model.framework}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">v{model.version}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(model.training_data_sources || []).slice(0, 2).map((s: string, i: number) => (
                        <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                      ))}
                      {(model.training_data_sources || []).length > 2 && (
                        <Badge variant="secondary" className="text-xs">+{(model.training_data_sources || []).length - 2}</Badge>
                      )}
                      {(model.training_data_sources || []).length === 0 && <span className="text-muted-foreground text-sm">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      model.status === "production" ? "bg-green-500/10 text-green-600" :
                      model.status === "testing" ? "bg-blue-500/10 text-blue-600" :
                      model.status === "development" ? "bg-amber-500/10 text-amber-600" :
                      "bg-muted text-muted-foreground"
                    }>{modelStatusLabels[model.status] || model.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{model.owner}</TableCell>
                  <TableCell className="text-sm">{model.department}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex flex-col items-center"><span className="font-medium">{model.accuracy || 0}%</span><Progress value={model.accuracy || 0} className="w-14 h-1 mt-1" /></div>
                  </TableCell>
                  <TableCell className="text-right font-medium">${(model.infra_cost || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => setEditing({ ...model })}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => onDeleteConfirm({ type: 'model', id: model.id, name: model.name })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>{filtered.length} de {aiModels.length} modelos</span>
            <div className="flex items-center gap-4">
              <span>En producción: <strong className="text-foreground">{aiModels.filter((m: any) => m.status === "production").length}</strong></span>
              <span>Costo infra total: <strong className="text-foreground">${aiModels.reduce((s: number, m: any) => s + (m.infra_cost || 0), 0).toLocaleString()}/mes</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Model Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Modelo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nombre</Label><Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} /></div>
                <div className="space-y-2"><Label>Tipo</Label><ModelTypeSelect value={editing.type || ''} onChange={(v) => setEditing({...editing, type: v})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Framework</Label>
                  <Select value={editing.framework || ''} onValueChange={(v) => setEditing({...editing, framework: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar framework" /></SelectTrigger>
                    <SelectContent>{FRAMEWORK_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Versión</Label><Input value={editing.version || ''} onChange={(e) => setEditing({...editing, version: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Responsable</Label><Input value={editing.owner || ''} onChange={(e) => setEditing({...editing, owner: e.target.value})} /></div>
                <div className="space-y-2">
                  <Label>Departamento</Label>
                  <Select value={editing.department || ''} onValueChange={(v) => setEditing({...editing, department: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar departamento" /></SelectTrigger>
                    <SelectContent>{businessAreas.map((a: any) => <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Descripción</Label><Textarea value={editing.description || ''} onChange={(e) => setEditing({...editing, description: e.target.value})} rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({...editing, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="development">Desarrollo</SelectItem>
                      <SelectItem value="testing">Testing</SelectItem>
                      <SelectItem value="production">Producción</SelectItem>
                      <SelectItem value="retired">Retirado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Accuracy (%)</Label><Input type="number" value={editing.accuracy || 0} onChange={(e) => setEditing({...editing, accuracy: parseInt(e.target.value) || 0})} /></div>
              </div>
              <div className="space-y-2"><Label>Costo Infra/mes (USD)</Label><Input type="number" value={editing.infra_cost || 0} onChange={(e) => setEditing({...editing, infra_cost: parseFloat(e.target.value) || 0})} /></div>
              <div className="space-y-2">
                <Label>Fuentes de Entrenamiento</Label>
                <DataSourceMultiSelect selected={editing.training_data_sources || []} onChange={(t) => setEditing({...editing, training_data_sources: t})} dataSources={dataSources} />
              </div>
              <Button onClick={handleEdit} className="w-full" disabled={updateModel.isPending}>
                {updateModel.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
